import { methodEffect } from '../api/access.js';
import { CORE_API_BASELINE } from '../api/baseline.js';

export type CommandEffect = 'read' | 'write' | 'local-write' | 'dynamic';
export type CommandSource = 'local' | 'core' | 'online' | 'mixed';
export type CommandOutput = 'json' | 'jsonl' | 'file';

export interface CommandDefinition {
  path: string;
  summary: string;
  usage: string;
  effect: CommandEffect;
  source: CommandSource;
  trust: 'trusted' | 'untrusted';
  confirmation: 'none' | 'required' | 'explicit-output';
  output: CommandOutput[];
  method?: string;
  capability?: string;
  minimumCore?: string;
  compatibility?: string;
}

interface DefinitionOptions {
  usage?: string;
  method?: string;
  output?: CommandOutput[];
  confirmation?: CommandDefinition['confirmation'];
  compatibility?: string;
}

const CORE_CONTRACT = `Core ${CORE_API_BASELINE.apiPath}`;

function command(
  path: string,
  summary: string,
  effect: CommandEffect,
  source: CommandSource,
  options: DefinitionOptions = {},
): CommandDefinition {
  const method = options.method;
  return {
    path,
    summary,
    usage: `zaparoo-cli ${options.usage ?? `${path} [options]`}`,
    effect,
    source,
    trust: source === 'local' ? 'trusted' : 'untrusted',
    confirmation:
      options.confirmation ??
      (effect === 'write' ? 'required' : effect === 'local-write' ? 'explicit-output' : 'none'),
    output: options.output ?? ['json'],
    method,
    capability: method,
    minimumCore: source === 'core' || source === 'mixed' ? CORE_CONTRACT : undefined,
    compatibility: options.compatibility,
  };
}

export const COMMAND_CATALOG: readonly CommandDefinition[] = [
  command('catalog', 'List the machine-readable CLI command contract.', 'read', 'local', {
    usage: 'catalog [--json] [--filter <text>]',
  }),
  command(
    'capabilities',
    'Inspect CLI coverage and safely probe Core compatibility.',
    'read',
    'mixed',
    {
      usage: 'capabilities [--device <host:port>] [--json]',
    },
  ),
  command(
    'agent install',
    'Install bundled skills into a project agent directory.',
    'write',
    'local',
    {
      usage:
        'agent install [--client <agents|claude|cursor|copilot>] [--target <path>] [--skill <name>] --yes',
    },
  ),
  command(
    'agent update',
    'Refresh an existing project-local Zaparoo skill installation.',
    'write',
    'local',
    {
      usage:
        'agent update [--client <agents|claude|cursor|copilot>] [--target <path>] [--skill <name>] --yes',
    },
  ),
  command(
    'agent doctor',
    'Check CLI, packaged skills, and project-local installation integrity.',
    'read',
    'local',
    {
      usage: 'agent doctor [--client <agents|claude|cursor|copilot>] [--target <path>]',
    },
  ),
  command(
    'docs list',
    'List bundled and fixed authoritative documentation sources.',
    'read',
    'local',
  ),
  command(
    'docs search',
    'Search bounded bundled documentation and source metadata.',
    'read',
    'local',
    {
      usage: 'docs search <query> [--limit <n>] [--json]',
    },
  ),
  command('docs get', 'Read a bundled document or bounded fixed HTTPS source.', 'read', 'mixed', {
    usage: 'docs get <source-id> [--output <path>] [--max-size <bytes>] [--json]',
    output: ['json', 'file'],
  }),
  command(
    'feedback',
    'Generate an official feedback link and private-data checklist.',
    'read',
    'local',
    {
      usage: 'feedback [--category <bug|skill|feature|security>] [--json]',
    },
  ),

  command('devices list', 'List configured Core devices and pairing presence.', 'read', 'local'),
  command('devices scan', 'Discover Core devices with bounded mDNS.', 'read', 'core', {
    usage: 'devices scan [--timeout <seconds>] [--json]',
  }),
  command('devices ping', 'Connect and read Core version and health.', 'read', 'core', {
    method: 'version',
  }),
  command('devices default show', 'Show configured default Core device.', 'read', 'local'),
  command('devices default set', 'Save a default Core device.', 'write', 'local', {
    usage: 'devices default set <host:port> --yes',
  }),
  command('devices default clear', 'Clear saved default Core device.', 'write', 'local', {
    usage: 'devices default clear --yes',
  }),
  command(
    'doctor',
    'Run ordered connectivity, authentication, encryption, and API checks.',
    'read',
    'mixed',
    {
      usage: 'doctor [--device <host:port>] [--json]',
      method: 'health',
    },
  ),

  command(
    'pair list',
    'List locally stored device pairing records without secrets.',
    'read',
    'local',
  ),
  command(
    'pair status',
    'Inspect stored pairing and verify the selected device.',
    'read',
    'mixed',
    {
      method: 'clients.current',
    },
  ),
  command('pair begin', 'Start Core-side pairing.', 'write', 'core', {
    usage: 'pair begin [--role <member|admin>] --yes',
    method: 'clients.pair.start',
  }),
  command(
    'pair complete',
    'Complete PIN pairing and save encrypted credentials.',
    'write',
    'mixed',
    {
      usage: 'pair complete [--pin <pin>] [--name <client-name>] --yes',
      method: 'clients.pair.start',
    },
  ),
  command('pair start', 'Compatibility alias for pair complete.', 'write', 'mixed', {
    usage: 'pair start [--pin <pin>] [--name <client-name>] --yes',
    method: 'clients.pair.start',
  }),
  command('pair cancel', 'Cancel Core-side pairing.', 'write', 'core', {
    usage: 'pair cancel --yes',
    method: 'clients.pair.cancel',
  }),
  command(
    'pair forget',
    'Delete locally stored credentials for selected device.',
    'write',
    'local',
    {
      usage: 'pair forget --yes',
    },
  ),
  command(
    'rpc',
    'Call a Core JSON-RPC method using conservative dynamic policy.',
    'dynamic',
    'core',
    {
      usage: "rpc <method> ['<json-params>'] [--json]",
      compatibility: 'Unknown methods are treated as writes by policy enforcement.',
    },
  ),

  command('media status', 'Read Core media database and work status.', 'read', 'core', {
    method: 'media',
  }),
  command('media search', 'Search indexed media with bounded filters and cursor.', 'read', 'core', {
    usage: 'media search [query] [--system <id>] [--max-results <n>] [--cursor <cursor>]',
    method: 'media.search',
  }),
  command('media browse', 'Browse indexed media paths.', 'read', 'core', {
    usage: 'media browse [path] [--system <id>] [--max-results <n>] [--cursor <cursor>]',
    method: 'media.browse',
  }),
  command('media browse-index', 'Read media browse index entries.', 'read', 'core', {
    usage: 'media browse-index [path] [--system <id>] [--sort <sort>]',
    method: 'media.browse.index',
  }),
  command('media active', 'Read active media as a lifecycle indication.', 'read', 'core', {
    method: 'media.active',
    compatibility: 'Active media does not prove the platform has finished launching or stopping.',
  }),
  command('media active-update', 'Replace active-media metadata.', 'write', 'core', {
    usage: 'media active-update --system-id <id> --media-path <path> --media-name <name> --yes',
    method: 'media.active.update',
  }),
  command('media history', 'Read paginated play history.', 'read', 'core', {
    usage: 'media history [--system <id>] [--limit <n>] [--cursor <cursor>]',
    method: 'media.history',
  }),
  command('media history-latest', 'Read latest media history entry.', 'read', 'core', {
    method: 'media.history.latest',
  }),
  command('media top', 'Read most-played media.', 'read', 'core', {
    usage: 'media top [--system <id>] [--since <time>] [--limit <n>]',
    method: 'media.history.top',
  }),
  command('media lookup', 'Resolve a media title in one system.', 'read', 'core', {
    usage: 'media lookup <name> --system <id> [--fuzzy-system]',
    method: 'media.lookup',
  }),
  command('media meta', 'Read media metadata.', 'read', 'core', {
    usage: 'media meta (--media-id <id> | --system <id> --path <path>)',
    method: 'media.meta',
  }),
  command('media meta-update', 'Update media launcher metadata.', 'write', 'core', {
    usage:
      'media meta-update (--media-id <id> | --system <id> --path <path>) --launcher <id> --yes',
    method: 'media.meta.update',
  }),
  command('media image', 'Download a media image to an explicit path.', 'local-write', 'core', {
    usage: 'media image (--media-id <id> | --system <id> --path <path>) --output <path>',
    method: 'media.image',
    output: ['file', 'json'],
  }),
  command('media tags', 'List media tags.', 'read', 'core', { method: 'media.tags' }),
  command('media tags-update', 'Add or remove media tags.', 'write', 'core', {
    usage:
      'media tags-update (--media-id <id> | --system <id> --path <path>) (--add <tag> | --remove <tag>) --yes',
    method: 'media.tags.update',
  }),
  command('media title-parse', 'Parse a media path into title metadata.', 'read', 'core', {
    usage: 'media title-parse --system-id <id> --path <path>',
    method: 'media.title.parse',
  }),
  command('media clean-orphans', 'Remove orphaned media records.', 'write', 'core', {
    usage: 'media clean-orphans --yes',
    method: 'media.clean.orphans',
  }),
  command('media control', 'Send a control action to active media.', 'write', 'core', {
    usage: 'media control <action> [--slot <slot>] [--arg <key=value>] --yes',
    method: 'media.control',
  }),
  command('media index status', 'Read media indexing progress.', 'read', 'core', {
    method: 'media',
  }),
  command('media index start', 'Start media indexing.', 'write', 'core', {
    usage: 'media index start [--system <id> | --rebuild] --yes',
    method: 'media.generate',
  }),
  command('media index cancel', 'Cancel media indexing.', 'write', 'core', {
    usage: 'media index cancel --yes',
    method: 'media.generate.cancel',
  }),
  command('media index resume', 'Resume media indexing.', 'write', 'core', {
    usage: 'media index resume --yes',
    method: 'media.generate.resume',
  }),
  command('media scrapers', 'List configured media scrapers.', 'read', 'core', {
    method: 'scrapers',
  }),
  command('media scrape status', 'Read media scraping progress.', 'read', 'core', {
    method: 'media.scrape.status',
  }),
  command('media scrape start', 'Start media scraping.', 'write', 'core', {
    usage: 'media scrape start --scraper <id> [--system <id>] [--force] --yes',
    method: 'media.scrape',
  }),
  command('media scrape cancel', 'Cancel media scraping.', 'write', 'core', {
    usage: 'media scrape cancel --yes',
    method: 'media.scrape.cancel',
  }),
  command('media scrape resume', 'Resume media scraping.', 'write', 'core', {
    usage: 'media scrape resume --yes',
    method: 'media.scrape.resume',
  }),

  command(
    'systems list',
    'List systems known to Core with optional local summary/filter.',
    'read',
    'core',
    {
      usage: 'systems list [--all] [--filter <text>] [--summary]',
      method: 'systems',
    },
  ),
  command('launchers list', 'List launchers known to Core.', 'read', 'core', {
    method: 'launchers',
  }),
  command('launchers refresh', 'Refresh Core launcher definitions.', 'write', 'core', {
    usage: 'launchers refresh --yes',
    method: 'launchers.refresh',
  }),
  command(
    'run',
    'Submit token text or ZapScript without assuming platform completion.',
    'write',
    'core',
    {
      usage: 'run <zapscript-or-text> [--type <type>] [--uid <uid>] [--unsafe] --yes',
      method: 'run',
      compatibility:
        'RPC success means accepted, not settled. Pace media.active checks and wait for the platform before another run or stop.',
    },
  ),
  command(
    'stop',
    'Request active media stop without assuming platform completion.',
    'write',
    'core',
    {
      usage: 'stop --yes',
      method: 'stop',
      compatibility:
        'RPC success means accepted, not settled. Pace media.active checks until clear and wait for the platform before another lifecycle command.',
    },
  ),
  command('readers list', 'List connected NFC readers.', 'read', 'core', { method: 'readers' }),
  command('readers write', 'Write text to an NFC tag.', 'write', 'core', {
    usage: 'readers write <text> [--reader <id>] --yes',
    method: 'readers.write',
  }),
  command('readers write-cancel', 'Cancel a reader write operation.', 'write', 'core', {
    usage: 'readers write-cancel [--reader <id>] --yes',
    method: 'readers.write.cancel',
  }),
  command('tokens list', 'Read active and latest tokens.', 'read', 'core', { method: 'tokens' }),
  command('tokens history', 'Read bounded token history.', 'read', 'core', {
    usage: 'tokens history [--limit <n>]',
    method: 'tokens.history',
  }),

  command('mappings list', 'List token mappings.', 'read', 'core', { method: 'mappings' }),
  command('mappings add', 'Create a token mapping.', 'write', 'core', {
    usage: 'mappings add --type <type> --match <match> --pattern <pattern> --yes',
    method: 'mappings.new',
  }),
  command('mappings update', 'Update a token mapping.', 'write', 'core', {
    usage: 'mappings update <id> [fields] --yes',
    method: 'mappings.update',
  }),
  command('mappings delete', 'Delete a token mapping.', 'write', 'core', {
    usage: 'mappings delete <id> --yes',
    method: 'mappings.delete',
  }),
  command('mappings reload', 'Reload token mappings.', 'write', 'core', {
    usage: 'mappings reload --yes',
    method: 'mappings.reload',
  }),
  command('settings get', 'Read Core settings.', 'read', 'core', { method: 'settings' }),
  command('settings update', 'Update Core settings.', 'write', 'core', {
    usage: 'settings update [fields | --params <json>] --yes',
    method: 'settings.update',
  }),
  command('settings reload', 'Reload Core settings.', 'write', 'core', {
    usage: 'settings reload --yes',
    method: 'settings.reload',
  }),

  command('clients list', 'List paired API clients.', 'read', 'core', {
    method: 'clients',
    compatibility:
      'Core restricts this method to localhost; remote clients should use clients current.',
  }),
  command('clients current', 'Read current API client identity.', 'read', 'core', {
    method: 'clients.current',
  }),
  command('clients delete', 'Delete a paired API client.', 'write', 'core', {
    usage: 'clients delete <client-id> --yes',
    method: 'clients.delete',
  }),
  command('clients pair-begin', 'Start Core-side API client pairing.', 'write', 'core', {
    usage: 'clients pair-begin [--role <member|admin>] --yes',
    method: 'clients.pair.start',
  }),
  command('clients pair-cancel', 'Cancel Core-side API client pairing.', 'write', 'core', {
    usage: 'clients pair-cancel --yes',
    method: 'clients.pair.cancel',
  }),
  command('profiles list', 'List device profiles.', 'read', 'core', { method: 'profiles' }),
  command('profiles active', 'Read active device profile.', 'read', 'core', {
    method: 'profiles.active',
  }),
  command('profiles new', 'Create a device profile.', 'write', 'core', {
    usage: 'profiles new --name <name> [fields] --yes',
    method: 'profiles.new',
  }),
  command('profiles update', 'Update a device profile.', 'write', 'core', {
    usage: 'profiles update <profile-id> [fields] --yes',
    method: 'profiles.update',
  }),
  command('profiles delete', 'Delete a device profile.', 'write', 'core', {
    usage: 'profiles delete <profile-id> --yes',
    method: 'profiles.delete',
  }),
  command('profiles switch', 'Switch active device profile.', 'write', 'core', {
    usage: 'profiles switch (--profile-id <id> | --switch-id <id>) [--pin <pin>] --yes',
    method: 'profiles.switch',
  }),
  command('profiles verify', 'Verify profile or switch credentials.', 'read', 'core', {
    usage: 'profiles verify (--profile-id <id> | --switch-id <id>) [--pin <pin>]',
    method: 'profiles.verify',
  }),

  command('ui state', 'Read current Core UI state.', 'read', 'core', { method: 'ui' }),
  command('ui respond', 'Respond to a Core UI event.', 'write', 'core', {
    usage: 'ui respond <id> --action <dismiss|select|confirm> [--choice-id <id>] --yes',
    method: 'ui.respond',
  }),
  command('confirm', 'Confirm staged launch-guard token.', 'write', 'core', {
    usage: 'confirm --yes',
    method: 'confirm',
  }),
  command('auth claim', 'Claim Core using a privately supplied token.', 'write', 'core', {
    usage: 'auth claim --claim-url <url> [--token -] --yes',
    method: 'settings.auth.claim',
  }),
  command('auth status', 'Read Core link status for one allowed URL.', 'read', 'core', {
    usage: 'auth status --url <https-url>',
    method: 'settings.auth.status',
  }),
  command('auth unlink', 'Unlink Core from Online.', 'write', 'core', {
    usage: 'auth unlink --yes',
    method: 'settings.auth.unlink',
  }),
  command('auth link', 'Start Core linking.', 'write', 'core', {
    usage: 'auth link [--url <url>] --yes',
    method: 'settings.auth.link',
  }),
  command('auth link-status', 'Read Core linking progress.', 'read', 'core', {
    method: 'settings.auth.link.status',
  }),
  command('auth link-cancel', 'Cancel Core linking.', 'write', 'core', {
    usage: 'auth link-cancel --yes',
    method: 'settings.auth.link.cancel',
  }),

  command('backup create', 'Create a local Core backup.', 'write', 'core', {
    usage: 'backup create --yes',
    method: 'settings.backup',
  }),
  command('backup list', 'List local Core backups.', 'read', 'core', {
    method: 'settings.backup.list',
  }),
  command('backup inspect', 'Inspect a local Core backup.', 'read', 'core', {
    usage: 'backup inspect <name>',
    method: 'settings.backup.inspect',
  }),
  command('backup delete', 'Delete a local Core backup.', 'write', 'core', {
    usage: 'backup delete <name> --yes',
    method: 'settings.backup.delete',
  }),
  command('backup restore', 'Restore a local Core backup.', 'write', 'core', {
    usage: 'backup restore <name> --yes',
    method: 'settings.backup.restore',
  }),
  command('backup status', 'Read backup operation status.', 'read', 'core', {
    method: 'settings.backup.status',
  }),
  command('backup remote-run', 'Create and upload a remote backup.', 'write', 'core', {
    usage: 'backup remote-run --yes',
    method: 'settings.backup.remote.run',
  }),
  command('backup remote-list', 'List remote backups.', 'read', 'core', {
    method: 'settings.backup.remote.list',
  }),
  command('backup remote-restore', 'Restore a remote backup.', 'write', 'core', {
    usage: 'backup remote-restore <id> --yes',
    method: 'settings.backup.remote.restore',
  }),
  command('playtime status', 'Read current playtime state.', 'read', 'core', {
    method: 'playtime',
  }),
  command('playtime limits get', 'Read playtime limits.', 'read', 'core', {
    method: 'settings.playtime.limits',
  }),
  command('playtime limits update', 'Update playtime limits.', 'write', 'core', {
    usage: 'playtime limits update [fields] --yes',
    method: 'settings.playtime.limits.update',
  }),
  command('update check', 'Check for Core updates.', 'read', 'core', { method: 'update.check' }),
  command('update apply', 'Apply a Core update.', 'write', 'core', {
    usage: 'update apply --yes',
    method: 'update.apply',
  }),

  command('admin health', 'Compatibility alias for Core health.', 'read', 'core', {
    method: 'health',
  }),
  command('admin update-check', 'Compatibility alias for update check.', 'read', 'core', {
    method: 'update.check',
  }),
  command('admin update-apply', 'Compatibility alias for update apply.', 'write', 'core', {
    usage: 'admin update-apply --yes',
    method: 'update.apply',
  }),
  command(
    'admin logs-download',
    'Compatibility alias for Core log download.',
    'local-write',
    'core',
    {
      usage: 'admin logs-download --output <path>',
      method: 'settings.logs.download',
      output: ['file', 'json'],
    },
  ),
  command('admin auth-claim', 'Compatibility alias for Core claim.', 'write', 'core', {
    usage: 'admin auth-claim --claim-url <url> [--token -] --yes',
    method: 'settings.auth.claim',
  }),
  command('admin playtime', 'Compatibility alias for playtime status.', 'read', 'core', {
    method: 'playtime',
  }),
  command('admin playtime-limits', 'Compatibility alias for playtime limits.', 'read', 'core', {
    method: 'settings.playtime.limits',
  }),
  command('inbox list', 'List Core inbox messages.', 'read', 'core', { method: 'inbox' }),
  command('inbox delete', 'Delete a Core inbox message.', 'write', 'core', {
    usage: 'inbox delete <id> --yes',
    method: 'inbox.delete',
  }),
  command('inbox clear', 'Clear Core inbox.', 'write', 'core', {
    usage: 'inbox clear --yes',
    method: 'inbox.clear',
  }),
  command('input keyboard', 'Send keyboard input.', 'write', 'core', {
    usage: 'input keyboard <keys> --yes',
    method: 'input.keyboard',
  }),
  command('input gamepad', 'Send gamepad input.', 'write', 'core', {
    usage: 'input gamepad <buttons> --yes',
    method: 'input.gamepad',
  }),
  command('screenshot', 'Capture display to an explicit local file.', 'local-write', 'core', {
    usage: 'screenshot --output <path>',
    method: 'screenshot',
    output: ['file', 'json'],
  }),
  command('state', 'Read a compact Core device-state snapshot.', 'read', 'core', {
    method: 'version',
  }),
  command('watch', 'Stream bounded Core notifications.', 'read', 'core', {
    usage: 'watch [--seconds <n>] [--methods <a,b>] --jsonl',
    output: ['json', 'jsonl'],
  }),
  command('logs trace', 'Read redacted local RPC traces.', 'read', 'local', {
    usage: 'logs trace [--last <n>]',
  }),
  command(
    'logs download',
    'Download current Core log to an explicit path.',
    'local-write',
    'core',
    {
      usage: 'logs download --output <path>',
      method: 'settings.logs.download',
      output: ['file', 'json'],
    },
  ),

  command('online auth set', 'Save an Online User API key from private input.', 'write', 'local', {
    usage: 'online auth set --yes',
  }),
  command('online auth status', 'Report Online credential presence and source.', 'read', 'local'),
  command('online auth forget', 'Delete saved Online User API key.', 'write', 'local', {
    usage: 'online auth forget --yes',
  }),
  command('online status', 'Read public Online User API status.', 'read', 'online'),
  command('online profile', 'Read account profile.', 'read', 'online'),
  command('online sessions list', 'Read paginated play sessions.', 'read', 'online', {
    usage: 'online sessions list [filters] [--limit <n>] [--cursor <cursor>] [--all-pages]',
  }),
  command('online sessions active', 'Read or watch active sessions.', 'read', 'online', {
    usage: 'online sessions active [--watch --seconds <n> --jsonl]',
    output: ['json', 'jsonl'],
  }),
  command('online sessions summary', 'Read paginated play-session summary.', 'read', 'online', {
    usage: 'online sessions summary [--group <group>] [--limit <n>] [--cursor <cursor>]',
  }),
  command('online cards list', 'Read paginated account cards.', 'read', 'online'),
  command('online decks list', 'Read paginated account decks.', 'read', 'online'),
  command('online decks get', 'Read one account deck.', 'read', 'online', {
    usage: 'online decks get <short-id>',
  }),
  command('online decks cards', 'Read paginated cards in a deck.', 'read', 'online', {
    usage: 'online decks cards <short-id> [--limit <n>] [--cursor <cursor>]',
  }),
  command('online devices list', 'Read paginated linked devices.', 'read', 'online'),
  command('online backups list', 'Read paginated device backups.', 'read', 'online', {
    usage: 'online backups list <device-id> [--limit <n>] [--cursor <cursor>]',
  }),
  command('online backups files', 'Read paginated backup files.', 'read', 'online', {
    usage: 'online backups files <device-id> <backup-id> [--category <category>]',
  }),
  command(
    'online backups download',
    'Download and verify a backup file.',
    'local-write',
    'online',
    {
      usage: 'online backups download <device-id> <backup-id> <sha256> --output <path>',
      output: ['file', 'json'],
      confirmation: 'required',
    },
  ),
  command('online request', 'GET an official Online User API /v1 path.', 'read', 'online', {
    usage: 'online request </v1/path>',
  }),
] as const;

const DEFAULT_PATHS: Record<string, string> = {
  agent: 'agent doctor',
  auth: 'auth status',
  backup: 'backup status',
  clients: 'clients list',
  devices: 'devices list',
  'devices default': 'devices default show',
  docs: 'docs list',
  inbox: 'inbox list',
  launchers: 'launchers list',
  logs: 'logs trace',
  mappings: 'mappings list',
  media: 'media search',
  'media index': 'media index status',
  'media scrape': 'media scrape status',
  online: 'online status',
  'online auth': 'online auth status',
  pair: 'pair status',
  playtime: 'playtime status',
  'playtime limits': 'playtime limits get',
  profiles: 'profiles list',
  readers: 'readers list',
  settings: 'settings get',
  systems: 'systems list',
  tokens: 'tokens list',
  ui: 'ui state',
  update: 'update check',
};

export function resolveCommandDefinition(positionals: string[]): CommandDefinition | undefined {
  const direct = [...COMMAND_CATALOG]
    .sort((a, b) => b.path.split(' ').length - a.path.split(' ').length)
    .find((entry) => pathMatches(entry.path, positionals));
  if (direct) return dynamicDefinition(direct, positionals);

  const key = positionals.slice(0, 2).join(' ');
  const fallback = DEFAULT_PATHS[key] ?? DEFAULT_PATHS[positionals[0] ?? ''];
  const entry = fallback
    ? COMMAND_CATALOG.find((candidate) => candidate.path === fallback)
    : undefined;
  return entry ? dynamicDefinition(entry, positionals) : undefined;
}

function pathMatches(path: string, positionals: string[]): boolean {
  const segments = path.split(' ');
  if (segments.length > positionals.length) return false;
  return segments.every((segment, index) => positionals[index] === segment);
}

function dynamicDefinition(
  definition: CommandDefinition,
  positionals: string[],
): CommandDefinition {
  if (definition.path !== 'rpc') return definition;
  const method = positionals[1];
  if (!method) return definition;
  const effect = methodEffect(method);
  return {
    ...definition,
    effect,
    confirmation: effect === 'write' ? 'required' : 'none',
    method,
    capability: method,
  };
}

export function catalogForPrefix(prefix?: string): CommandDefinition[] {
  const normalized = prefix?.trim().toLowerCase();
  if (!normalized) return [...COMMAND_CATALOG];
  return COMMAND_CATALOG.filter((entry) =>
    `${entry.path} ${entry.summary} ${entry.method ?? ''}`.toLowerCase().includes(normalized),
  );
}
